/**
 * components/accounting/TransactionForm.tsx
 * Simple form to add a new sales transaction with gateway, amount, and deposit date.
 * - Fees removed: net equals gross.
 * - Locked when selected month is closed.
 * - Entry users limited to current month date range.
 * - Viewer: form appears but is fully disabled (read-only).
 */

import { useState, useMemo } from 'react'
import { useAccountingStore } from '../../store/accounting'
import type { Gateway } from '../../types/accounting'
import { toast } from 'sonner'
import { useSelectedMonth } from '../../hooks/useMonth'
import { useAccountingStore as useAcc } from '../../store/accounting'
import { useAuthStore } from '../../store/auth'
import { useUserStore } from '@/store/user'

/**
 * TransactionForm
 * Adds new transaction and previews net amount.
 * Viewer: UI visible but disabled.
 */
export function TransactionForm() {

  const addTransaction = useUserStore((s) => s.addTransaction)
  const gateways = useUserStore((s) => s.gateways)

  const { month, range } = useSelectedMonth()
  const getClosure = useAcc((s) => s.getClosure)
  const isClosed = Boolean(getClosure(month))

  const role = useUserStore((s) => s.role)
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'

  const [description, setDescription] = useState('')
  const [gateway, setGateway] = useState<Gateway>(() => gateways[0]?.name || 'Mada')
  const [amountGross, setAmountGross] = useState<string>('')

  const defaultDate = useMemo(() => {
    const d = new Date()
    // If entry user, clamp to current month
    if (!isAdmin) {
      const start = new Date(range.start)
      const end = new Date(range.end)
      if (d < start) return start.toISOString().slice(0, 10)
      if (d > end) return end.toISOString().slice(0, 10)
    }
    return d.toISOString().slice(0, 10)
  }, [isAdmin, range.start, range.end])

  const [depositDate, setDepositDate] = useState<string>(defaultDate)

  const grossNum = Number(amountGross || 0)
  const net = Math.max(0, Number.isFinite(grossNum) ? grossNum : 0)

  // Disable form if month closed OR viewer role
  const disabled = isClosed || isViewer

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (disabled) {
      toast.error(isViewer ? 'لديك صلاحية مشاهدة فقط' : 'هذا الشهر مُقفَل. افتح الشهر لإضافة عمليات.')
      return
    }
    // For entry users, ensure date inside current month
    if (!isAdmin) {
      const d = new Date(`${depositDate}T00:00:00`)
      if (d < new Date(range.start) || d > new Date(range.end)) {
        toast.error('يمكن الإدخال ضمن الشهر الحالي فقط')
        return
      }
    }

    const gross = Number(amountGross)
    if (!depositDate) {
      toast.error('الرجاء تحديد تاريخ الإيداع')
      return
    }
    if (!Number.isFinite(gross) || gross <= 0) {
      toast.error('الرجاء إدخال مبلغ إجمالي صحيح')
      return
    }
    if (!gateway) {
      toast.error('الرجاء اختيار بوابة الدفع')
      return
    }

    addTransaction({
      description,
      gateway,
      amount_gross: gross,
      deposit_date: depositDate,
    })

    toast.success('تمت إضافة العملية بنجاح')
    setDescription('')
    setAmountGross('')
    // keep same depositDate and gateway for speed
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {isClosed && (
        <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">هذا الشهر مُقفَل ولا يمكن إضافة عمليات.</div>
      )}
      {isViewer && (
        <div className="rounded-lg bg-slate-50 p-2 text-sm text-slate-700">وضع مشاهدة فقط — النموذج معطّل.</div>
      )}
      <div>
        <label className="mb-1 block text-sm text-slate-600">الوصف</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="مثال: طلب رقم 1234"
          disabled={disabled}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-slate-600">بوابة الدفع</label>
          <select
            value={gateway}
            onChange={(e) => setGateway(e.target.value as Gateway)}
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            {gateways.length === 0 ? (
              <option value="">لا توجد بوابات</option>
            ) : (
              gateways.map((g) => (
                <option key={g.id} value={g.name}>
                  {g.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">تاريخ الإيداع</label>
          <input
            type="date"
            value={depositDate}
            onChange={(e) => setDepositDate(e.target.value)}
            disabled={disabled}
            min={!isAdmin ? new Date(range.start).toISOString().slice(0, 10) : undefined}
            max={!isAdmin ? new Date(range.end).toISOString().slice(0, 10) : undefined}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-slate-600">المبلغ</label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amountGross}
            onChange={(e) => setAmountGross(e.target.value)}
            placeholder="0.00"
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">الصافي</label>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900">
            <span className="text-slate-700">سيتم إيداع</span>
            <span className="font-semibold">
              {net.toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ريال
            </span>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={disabled}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          إضافة العملية
        </button>
      </div>
    </form>
  )
}
